import React from 'react';
import { FormattedMessage } from 'react-intl';
import { differenceWith, isEqual, filter, partition, property, uniq } from 'lodash-es';
import moment from 'moment';

const DATE_FORMAT = 'MMM Do YYYY, hh:mmA';

export const PUBLISH_DATE_NOW = 'PUBLISH_DATE_NOW';
export const PUBLISH_DATE_LATER = 'PUBLISH_DATE_LATER';
export const FIRST_DATE_PUBLISHED = 'FIRST_DATE_PUBLISHED';
export const SECOND_DATE_PUBLISHED = 'SECOND_DATE_PUBLISHED';

export const STATUS_PUBLISHED = 'published';
export const STATUS_CHANGE_ACCESS_PENDING = 'change-access-pending';
export const STATUS_SCHEDULED = 'scheduled';
export const STATUS_FAILED = 'failed';
export const STATUS_DRAFT = 'draft';
export const STATUS_IMPORTING = 'importing';

export const ALL_LISTENERS_TIER = {
  id: 'all_listeners',
  name: 'All Listeners',
  checked: true,
};

export const DRAFT_TIER = {
  id: 'draft',
  name: 'Draft',
  checked: true,
};

export const STATIC_TIERS = {
  all_listeners: 'All Listeners',
  public_only: 'Public Only',
};

export const TIER_CATEGORIES = {
  acastPlus: 'acastPlus',
  public: 'public',
};

export const TIMELINE_STEP_INDEX = {
  [PUBLISH_DATE_NOW]: 1,
  [PUBLISH_DATE_LATER]: 1,
  [FIRST_DATE_PUBLISHED]: 2,
  [SECOND_DATE_PUBLISHED]: 3,
};

export const ensureSelectedTiers = (selected, total = []) => {
  const filteredTiers = filter(total, ({ id }) => {
    return selected.find((tierId) => id === tierId);
  });

  const totalSelectedTiers = filteredTiers.map((tier) => ({ ...tier, checked: true }));
  return totalSelectedTiers.length ? totalSelectedTiers : [ALL_LISTENERS_TIER];
};

export const getPublishSequenceTiers = (publishSequence, idx = 0) => {
  const firstPublishSettings = publishSequence[idx];
  const plusTiersSelected = property(['integrations', 'acastPlus'])(firstPublishSettings) || [];
  const publicTiersSelected = property(['integrations', 'public'])(firstPublishSettings) || [];

  return [...plusTiersSelected, ...publicTiersSelected];
};

export const getNonPublishingSequenceTiers = (episodeIntegrations) => {
  const plusTiersSelected = property(['acastPlus', 'tiers'])(episodeIntegrations) || [];
  const publicTiersSelected = property(['public', 'tiers'])(episodeIntegrations) || [];

  return [...plusTiersSelected, ...publicTiersSelected];
};

export const getInitialTiersFromPublishSequence = (
  tiers = [],
  episodeIntegrations,
  publishSequence = [],
  idx = 0
) => {
  if (publishSequence?.length) {
    const selectedTiers = getPublishSequenceTiers(publishSequence, idx);

    const totalSelectedTiers = ensureSelectedTiers([...selectedTiers], tiers);
    return totalSelectedTiers.map((tier) => tier);
  } else {
    const selectedTiers = getNonPublishingSequenceTiers(episodeIntegrations);
    const totalSelectedTiers = ensureSelectedTiers([...selectedTiers], tiers);
    return totalSelectedTiers.map((tier) => tier);
  }
};

export const getTiersBasedOnAllListenersSelection = ({
  selectedTiers,
  newSelectedTiers,
  totalTiers,
  tiersMap,
}) => {
  const previouslySelectedAllListeners = selectedTiers.find(
    ({ id }) => id === ALL_LISTENERS_TIER.id
  );

  const selectedNewOptions = newSelectedTiers.length > 1;

  // when we have All Listeners selected and then select something else
  // deselect All Listeners option
  if (previouslySelectedAllListeners && selectedNewOptions) {
    totalTiers = totalTiers.filter(({ id }) => id !== ALL_LISTENERS_TIER.id);
  }

  const allListenersSelected = newSelectedTiers.includes(ALL_LISTENERS_TIER.id);

  // when we select All Listeners - filter out any selected option
  if (allListenersSelected) {
    totalTiers = totalTiers.filter(({ id }) => id === ALL_LISTENERS_TIER.id);
  }

  // select the latest selected id when deselecting All Listeners option
  if (!totalTiers.length) {
    const latestSelectionId = newSelectedTiers[newSelectedTiers.length - 1];

    if (latestSelectionId) {
      totalTiers.push(tiersMap[latestSelectionId]);
    }
  }

  return totalTiers;
};

export const getAvailablePublicTiers = (show, availableTiers = []) => {
  const publicTiers = show?.publicTiers?.map((tier) => ({ ...tier, type: 'public' })) || [];

  if (availableTiers && availableTiers.length) {
    const typedAvailableTiers = availableTiers.map((tier) => ({ ...tier, type: 'member' }));
    return publicTiers.concat(typedAvailableTiers);
  }

  return publicTiers;
};

export const getTiersIdNameMap = (tiers) => {
  const formattedTiers = [ALL_LISTENERS_TIER, ...tiers];
  const tiersMap = {};

  formattedTiers.forEach((t) => (tiersMap[t.id] = t));
  return tiersMap;
};

export const getFormattedSelectedTiers = ({ tiers, selectedTiers, newSelectedTiers }) => {
  if (newSelectedTiers.length === selectedTiers.length) {
    return newSelectedTiers;
  }

  const tiersMap = getTiersIdNameMap(tiers);

  let totalTiers = newSelectedTiers.map((tierId) => ({
    ...tiersMap[tierId],
    checked: true,
  }));

  // Make public tiers incompatible with plus tiers and vice versa
  const diff = differenceWith(
    newSelectedTiers,
    selectedTiers.map(({ id }) => id),
    isEqual
  );

  totalTiers =
    diff[0] && tiersMap[diff[0]].type === 'public'
      ? totalTiers.filter(({ type }) => type === 'public')
      : totalTiers.filter(({ type }) => type !== 'public');

  const finalTiers = getTiersBasedOnAllListenersSelection({
    selectedTiers,
    newSelectedTiers,
    totalTiers,
    tiersMap,
  });

  return finalTiers;
};

export const getPublishingDate = (episode, idx = 0) => {
  const publishSequence = episode?.publishSequence;
  const episodePublishDate = episode?.publishDate;

  const publishingSettings = publishSequence?.length ? publishSequence[idx] : [];
  const dateSelected = publishSequence?.length
    ? property('date')(publishingSettings)
    : episodePublishDate;

  const formattedDateSelected = moment(new Date(dateSelected)).format(DATE_FORMAT);
  const formattedEpisodePublishDate = moment(new Date(episodePublishDate)).format(DATE_FORMAT);

  // either return first or second publishing date depending on index
  if (formattedDateSelected === formattedEpisodePublishDate) {
    return episodePublishDate;
  }

  return dateSelected;
};

export const getCurrentStep = (firstPublishDate, secondPublishDate) => {
  if (firstPublishDate === null) {
    return PUBLISH_DATE_NOW;
  }

  const firstDateHasPublished = moment(firstPublishDate).isBefore(moment());

  const secondDateHasPublished =
    firstDateHasPublished && secondPublishDate && moment(secondPublishDate).isBefore(moment());

  if (secondDateHasPublished) {
    return SECOND_DATE_PUBLISHED;
  } else if (firstDateHasPublished) {
    return FIRST_DATE_PUBLISHED;
  }

  return PUBLISH_DATE_LATER;
};

export const getCurrentStepIndex = (firstPublishDate, secondPublishDate) => {
  const step = getCurrentStep(firstPublishDate, secondPublishDate);
  return TIMELINE_STEP_INDEX[step];
};

export const getHasNotFinishedPublishing = (episode) => {
  const { publishSequence, status } = episode;

  if (!publishSequence || !publishSequence.length || status === 'draft') {
    return false;
  }

  const [firstPublish, secondPublish] = publishSequence;

  // alternate check for some earlier change access episodes that did not
  // have the done attribute updated properly
  if (!firstPublish.done && !secondPublish.done) {
    const currentStep = getCurrentStep(firstPublish.date, secondPublish.date);
    return currentStep !== SECOND_DATE_PUBLISHED;
  }

  const hasNotFinished = publishSequence.some((sequence) => !sequence.done);
  return hasNotFinished;
};

export const getFilteredSelectedTableRows = (selectedRows) =>
  selectedRows.filter((row) => !getHasNotFinishedPublishing(row));

export const getChangeAccessMessageIntl = (plusTiers, currentStatus, episode) => {
  const { publishSequence } = episode;

  const dateOne = moment(getPublishingDate(episode, 0)).format(DATE_FORMAT);
  const dateTwo = moment(getPublishingDate(episode, 1)).format(DATE_FORMAT);

  const tiersOne = ensureSelectedTiers(getPublishSequenceTiers(publishSequence, 0), plusTiers)
    .map((tiers) => tiers.name)
    .join(', ');
  const tiersTwo = ensureSelectedTiers(getPublishSequenceTiers(publishSequence, 1), plusTiers)
    .map((tiers) => tiers.name)
    .join(', ');

  const ComposedMessage = () => (
    <div>
      <p>
        <FormattedMessage
          defaultMessage="PUBLISHED to {tiers} on {date}."
          values={{ tiers: tiersOne, date: dateOne }}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="SCHEDULED to {tiers} on {date}."
          values={{ tiers: tiersTwo, date: dateTwo }}
        />
      </p>
      <p>
        <FormattedMessage defaultMessage="To make changes, navigate to this episode's publishing settings." />
      </p>
    </div>
  );

  return {
    hoverMessage: <ComposedMessage />,
    currentStatus,
  };
};

export function ensureISODateString(value) {
  return typeof value === 'string' ? value : value.toISOString();
}

export function sanitizeTiers(tiers = []) {
  return tiers.map((el) => {
    if (typeof el === 'object') {
      return el.id;
    } else {
      return el;
    }
  });
}

export function validateTiers({ plusTiers = [], show }) {
  // Grab show-level public tiers IDs that are available for episodes.
  const availablePublicTiers = show?.publicTiers?.map(({ id }) => id) || [];

  const sanitizedPlusTiers = sanitizeTiers(plusTiers).filter((id) => id !== ALL_LISTENERS_TIER.id);

  // Partition the actual plus tiers from the show-level public tiers. The `plusTiers` is
  // the form field for selected tiers for a plus show which can include the public-only tier.
  const [publicPlusTiers, privatePlusTiers] = partition(sanitizedPlusTiers, (id) =>
    availablePublicTiers.includes(id)
  );

  return {
    plusTiers: privatePlusTiers,
    publicTiers: uniq([...publicPlusTiers]),
  };
}

export function buildPublishSequence({ currentDate, form, show }) {
  // Retrieve integration tiers for first sequence item
  const { plusTiers, publicTiers } = validateTiers({
    plusTiers: form.plusTiers,
    show,
  });

  // Retrieve integration tiers for second sequence item
  const { plusTiers: plusTiersTwo, publicTiers: publicTiersTwo } = validateTiers({
    plusTiers: form.plusTiersTwo,
    show,
  });

  if (!form.publishDateTwo) {
    return [];
  }

  const publishSequence = [
    {
      date: ensureISODateString(form.publishDate || currentDate),
      integrations: {
        ...(plusTiers && { acastPlus: plusTiers }),
        ...(publicTiers && { public: publicTiers }),
      },
      // Must be set to false if scheduled publish so that the
      // scheduler will notice it and update episode status.
      done: form.publishDate ? currentDate > new Date(form.publishDate) : false,
    },
    {
      date: ensureISODateString(form.publishDateTwo),
      integrations: {
        ...(plusTiersTwo && { acastPlus: plusTiersTwo }),
        ...(publicTiersTwo && { public: publicTiersTwo }),
      },
      done: currentDate > new Date(form.publishDateTwo),
    },
  ];

  return publishSequence;
}

export function getActivePublicSequenceItem(sequence) {
  const reversedSequence = [...sequence].reverse();
  const activeSequence = reversedSequence.find(({ done }) => !!done);

  // Fallback to first sequence item
  return activeSequence || sequence[0];
}

export function getPubDateAndIntegrationsFromSequence(sequence) {
  const activePublishSequenceItem = getActivePublicSequenceItem(sequence);

  const publishDate = activePublishSequenceItem.date;
  const integrations = Object.keys(activePublishSequenceItem.integrations).reduce((acc, key) => {
    return { ...acc, [key]: { tiers: activePublishSequenceItem.integrations[key] } };
  }, {});

  return { publishDate, integrations };
}

export function getPubDateAndIntegrationFallback({ currentDate, form, show }) {
  const { plusTiers, publicTiers } = validateTiers({
    plusTiers: form.plusTiers,
    show,
  });

  const publishDate = ensureISODateString(form.publishDate || currentDate);

  const integrations = {
    acastPlus: {
      tiers: plusTiers,
    },
    public: {
      tiers: publicTiers,
    },
    customPaywall: {
      locked: form.isLocked || false,
    },
  };

  return { publishDate, integrations };
}
